/**
 * Shared utilities for unified popup top section across IB / Saxo / Fidelity / Fidelity GS popups.
 *
 * Provides:
 *  - RANGE_OPTIONS: button list (1D, 1W, 1M, 3M, 6M, 1Y, YTD)
 *  - computePerf(valueHistory, period): { abs, pct, fromDate } for the selected period
 *  - hasEnoughHistory(valueHistory, period): true if any snapshot exists at/before the period cutoff
 *  - filterHistoryToRange(valueHistory, period): subset of snapshots within the range (for chart)
 *  - computeYDomain(chartData): [min, max] padded so the chart never starts from 0
 *  - periodKpiLabel(period): human label for the dynamic 3rd KPI ("Week-to-date", "1-Month", etc.)
 *
 * No popup-specific styling is bundled here — each popup keeps its own CSS classes.
 */

export const RANGE_OPTIONS = [
  { label: '1D',  days: 1   },
  { label: '1W',  days: 7   },
  { label: '1M',  days: 30  },
  { label: '3M',  days: 90  },
  { label: '6M',  days: 180 },
  { label: '1Y',  days: 365 },
  { label: 'YTD', days: null, ytd: true },
];

const KPI_LABELS = {
  '1D':  'Today',
  '1W':  'Week-to-date',
  '1M':  '1-Month',
  '3M':  '3-Month',
  '6M':  '6-Month',
  '1Y':  '1-Year',
  'YTD': 'Year-to-date',
};

export function periodKpiLabel(period) {
  return KPI_LABELS[period?.label] || period?.label || '';
}

/**
 * Resolve the cutoff Date for a given period option, anchored at `now` (or current time).
 * Returns null if neither days nor ytd is set.
 */
function periodCutoff(period, nowDate) {
  const now = nowDate ? new Date(nowDate) : new Date();
  if (period?.ytd) return new Date(now.getFullYear(), 0, 1);
  if (period?.days != null) {
    const c = new Date(now);
    c.setDate(c.getDate() - period.days);
    return c;
  }
  return null;
}

/**
 * Find the snapshot closest to (and at or before) the cutoff. Returns null if none qualifies.
 * `sorted` must be ascending by timeOfUpdate.
 */
function snapshotAtOrBefore(sorted, cutoff) {
  if (!sorted.length || !cutoff) return null;
  let candidate = null;
  for (const h of sorted) {
    if (new Date(h.timeOfUpdate) <= cutoff) candidate = h;
    else break;
  }
  return candidate;
}

function sortAsc(valueHistory) {
  return [...(valueHistory || [])].sort(
    (a, b) => new Date(a.timeOfUpdate) - new Date(b.timeOfUpdate)
  );
}

/**
 * Compute the period performance: absolute change and percent change from the snapshot at
 * (or before) the cutoff to the most recent snapshot.
 *
 * Returns { abs, pct, fromDate, toDate } or null if insufficient history.
 */
export function computePerf(valueHistory, period) {
  const sorted = sortAsc(valueHistory);
  if (sorted.length < 2) return null;

  const now = sorted[sorted.length - 1];
  const cutoff = periodCutoff(period, now.timeOfUpdate);
  if (!cutoff) return null;

  // For 1D specifically: compare last snapshot to the immediately prior one.
  // Daily cron means consecutive snapshots represent the closing-to-closing move,
  // which is what a "Today" move should show.
  let past;
  if (period?.days === 1) {
    past = sorted[sorted.length - 2];
  } else {
    past = snapshotAtOrBefore(sorted.slice(0, -1), cutoff);
  }
  if (!past || past.eurValue == null || past.eurValue === 0) return null;

  const abs = (now.eurValue ?? 0) - past.eurValue;
  const pct = (abs / past.eurValue) * 100;
  return { abs, pct, fromDate: past.timeOfUpdate, toDate: now.timeOfUpdate };
}

/**
 * True if valueHistory spans far enough back to compute a meaningful number for this period.
 * Used to disable buttons when the account has too little history.
 */
export function hasEnoughHistory(valueHistory, period) {
  const sorted = sortAsc(valueHistory);
  if (sorted.length < 2) return false;

  // 1D is just "do we have at least 2 snapshots".
  if (period?.days === 1) return true;

  const now = sorted[sorted.length - 1];
  const cutoff = periodCutoff(period, now.timeOfUpdate);
  if (!cutoff) return false;

  const earliest = new Date(sorted[0].timeOfUpdate);
  return earliest <= cutoff;
}

/**
 * Subset of snapshots within the selected period, returned as { date, eurValue } pairs
 * suitable for a recharts LineChart.
 *
 * For 1D we also include the prior snapshot so the line has 2 points to render.
 */
export function filterHistoryToRange(valueHistory, period) {
  const sorted = sortAsc(valueHistory);
  if (!sorted.length) return [];

  if (period?.days === 1) {
    // Include only the last 2 snapshots (yesterday + today)
    const tail = sorted.slice(-2);
    return tail.map(h => ({ date: h.timeOfUpdate, eurValue: h.eurValue }));
  }

  const now = sorted[sorted.length - 1];
  const cutoff = periodCutoff(period, now.timeOfUpdate);
  if (!cutoff) return sorted.map(h => ({ date: h.timeOfUpdate, eurValue: h.eurValue }));

  const filtered = sorted.filter(h => new Date(h.timeOfUpdate) >= cutoff);
  return filtered.map(h => ({ date: h.timeOfUpdate, eurValue: h.eurValue }));
}

/**
 * Y-axis domain that pads above max and below min, so the chart never visually starts from zero.
 * Pads roughly 10% of the range on each side, with a sensible floor (3% of max) when the range is tiny,
 * then rounds out to "nice" round numbers so the gridlines look clean.
 *
 * Returns [yMin, yMax] in raw numeric values for recharts.
 */
export function computeYDomain(chartData) {
  const values = (chartData || []).map(d => d.eurValue).filter(v => v != null && !isNaN(v));
  if (!values.length) return ['auto', 'auto'];

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;

  // Pad: the larger of 10% of the visible range, or 3% of the max value
  const pad = Math.max(range * 0.10, Math.abs(max) * 0.03);

  let yMin = min - pad;
  let yMax = max + pad;

  // Don't go below zero for portfolio values (accounts can't be negative for our case)
  if (min >= 0) yMin = Math.max(0, yMin);

  // Round to nice numbers based on the magnitude of the range
  const span = yMax - yMin;
  let step;
  if (span >= 1_000_000)      step = 100_000;
  else if (span >= 200_000)   step = 50_000;
  else if (span >= 50_000)    step = 10_000;
  else if (span >= 10_000)    step = 5_000;
  else if (span >= 1_000)     step = 1_000;
  else                        step = 100;

  yMin = Math.floor(yMin / step) * step;
  yMax = Math.ceil(yMax / step) * step;

  return [yMin, yMax];
}
